import { useLocation } from '@reach/router';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import React, { memo, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Blok } from '../../interfaces/Blok.interface';
import { getValidationSchema } from './QuoteFormValidation';
import { InitialValueProps } from './QuoteFormProps';

interface QuoteFormProps {
  sections: Blok[];
  onSubmit: (values: any) => void;
  initValues?: { [key: string]: string };
  isSubmitting?: boolean;
  propertyType?: string;
}

interface FieldBlok extends Blok {
  key: string;
  left_column_form_field?: FieldBlok[];
  right_column_form_field?: FieldBlok[];
  form_fields?: FieldBlok[];
  validation_rule?: string;
  error_message?: string;
  input_type?: string;
}

interface FormSection extends Blok {
  form_fields: FieldBlok[];
}

const formSectionsToShow = (sections: any, productType: string) => {
  return sections.filter((formSection: any) => {
    if (formSection.component !== 'conditional_form_section') return true;
    return formSection.show_if_product_is_selected === productType;
  });
};

const getFormFields = (
  sections: FormSection[]
): { [key: string]: FieldBlok } => {
  const initialValues: { [key: string]: FieldBlok } = {};

  const addFields = (fields?: FieldBlok[]) => {
    fields?.forEach((field) => {
      if (
        field.key &&
        field.key !== 'buy-address' &&
        field.key !== 'sell-address'
      ) {
        initialValues[field.key] = field;
      }

      addFields(field.left_column_form_field);
      addFields(field.right_column_form_field);
      addFields(field.form_fields);
    });
  };

  for (const section of sections) {
    addFields(section.form_fields);

    if (section.component === 'quote_total_terms_and_buttons') {
      initialValues['accept-terms-and-conditions'] = {
        key: 'accept-terms-and-conditions',
      } as FieldBlok;
    }
  }

  return initialValues;
};

const getInitialValues = (fields: any, productType: string) => {
  const isSellingProductType = ['selling', 'buying-and-selling'].includes(
    productType
  );
  const isRemortgagingProductType = ['remortgaging'].includes(productType);

  const addSameAddressCheckbox =
    isSellingProductType || isRemortgagingProductType;

  return Object.keys(fields).reduce((values, key) => {
    const preSelected_option =
      fields[key]?.preselected_option?.length &&
      fields[key]?.preselected_option[0].value;

    const currentAddressCheckboxKeys =
      key === 'isSellAddressSameAsCustomerAddress' ||
      key === 'isRemortgageAddressSameAsCustomerAddress';

    if (addSameAddressCheckbox && currentAddressCheckboxKeys) {
      return {
        ...values,
        [key]:
          (key === 'isSellAddressSameAsCustomerAddress' &&
            isSellingProductType) ||
          (key === 'isRemortgageAddressSameAsCustomerAddress' &&
            isRemortgagingProductType),
      };
    }

    return {
      ...values,
      [key]:
        key === 'accept-terms-and-conditions'
          ? false
          : preSelected_option || '',
    };
  }, {});
};

const QuoteForm: React.FC<QuoteFormProps> = ({
  sections,
  onSubmit,
  initValues,
  propertyType,
}) => {
  const location = useLocation();
  const [productType, setProductType] = useState<string>('buying');
  const fields = getFormFields(sections as FormSection[]);
  const initialValues: InitialValueProps = {
    ...getInitialValues(fields, productType),
    ...initValues,
  };

  const filteredSections = formSectionsToShow(sections, productType);
  const filteredFields = getFormFields(filteredSections);
  const [isClientReady, setIsClientReady] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // if we have it from the quote, use that, if not check the query params
    setProductType(propertyType || searchParams.get('type') || 'buying');
    setIsClientReady(true);
  }, [location]);

  const ValidationSchema = Yup.object().shape(
    getValidationSchema(filteredFields)
  );

  if (!isClientReady) return null;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={ValidationSchema}
      enableReinitialize={true}
    >
      {({ values }: { values: any }) => {
        // Step 2: Update the URL query param when the conveyancy-choice value changes
        React.useEffect(() => {
          const currentTypeValue = new URLSearchParams(location.search).get(
            'type'
          );
          const fieldValue = values['conveyancy-choice'];

          if (fieldValue && fieldValue !== currentTypeValue) {
            const newSearchParams = new URLSearchParams(location.search);
            newSearchParams.set('type', fieldValue);
            navigate(`${location.pathname}?${newSearchParams.toString()}`, {
              replace: true, // Use true to avoid adding entries to the history stack
            });
          }
        }, [values['conveyancy-choice'], location.pathname]); // Only re-run if conveyancy-choice or pathname changes

        return (
          <Form>
            {filteredSections.map((section: Blok, index: number) => {
              return (
                <StoryblokComponent
                  blok={{
                    ...section,
                    sectionNumber: index + 1,
                    selectedProductType: productType,
                  }}
                  key={section._uid}
                />
              );
            })}
          </Form>
        );
      }}
    </Formik>
  );
};

export default memo(QuoteForm);
